import React from 'react';
import {
    Drawer,
    TextField,
    Tooltip,
    IconButton,
    FormControlLabel,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button
} from '@material-ui/core/';
import Moment from 'moment-timezone';

import ImportIcon from '@material-ui/icons/CloudUpload';
import DownloadIcon from '@material-ui/icons/CloudDownload';
import DetectIcon from '@material-ui/icons/TrackChanges';
import ImportEMRIcon from '@material-ui/icons/LocalHospital';

import PatientView from './PatientView';
import EnhancedTable from "@apricityhealth/web-common-lib/components/EnhancedTable";
import ImportPatientsDialog from '../../dialogs/ImportPatientsDialog';
import DetectAllDialog from '../../dialogs/DetectAllDialog';

import {
    getPatientsPage,
    importPatientsEMR
} from '@apricityhealth/web-common-lib/utils/Services';
import { getErrorMessage } from '@apricityhealth/web-common-lib/utils/getErrorMessage';

import SelectPlan from '@apricityhealth/web-common-lib/components/SelectPlan';
import SelectOrg from '@apricityhealth/web-common-lib/components/SelectOrg';
import SelectTrial from '@apricityhealth/web-common-lib/components/SelectTrial';
import ExportPatients from './ExportPatients';

const DATETIME_FORMAT = 'yyyy-MM-DDTHH:mm';


export class PatientsView extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            error: null,
            progress: null,
            search: '',
            patients: [],
            planId: '*',
            orgId: '*',
            trialId: '*',
            showExported: false,
            showImported: false,
            showInactive: false,
            showDates: false,
            importOptions: {
                fullImport: false,
                importConditions: true,
                importMedications: true,
                importProcedures: true,
                importDocuments: true,
                importReports: true,
                importEncounters: true,
                sinceDate: undefined,
                startDate: undefined,
                endDate: undefined
            }
        }
    }

    componentWillMount() {
        let parts = window.location.pathname.slice(1).split('/');
        if (parts.length > 1) {
            this.onViewPatient(parts[1]);
        }
    }

    loadContent({ offset, limit, order, orderBy }) {
        const { appContext } = this.props;
        let { search, planId, orgId, trialId, showExported, showImported, showInactive, showDates } = this.state;

        return new Promise((resolve, reject) => {
            const opts = {
                offset,
                limit,
                order,
                orderBy
            };
            if (search) {
                opts.search = search;
            }
            if (planId && planId !== '*') {
                opts.planId = planId;
            }
            if (orgId && orgId !== '*') {
                opts.orgId = orgId;
            }
            if (trialId && trialId !== '*') {
                opts.trialId = trialId;
            }
            if (showExported) {
                opts.exportFlag = true;
            }
            if (showInactive) {
                opts.inactive = true;
            } else {
                opts.inactive = false;
            }
            if (showImported) {
                opts.importId = '*';
            }
            if (showDates) {
                opts.project = 'patientCreated,consentGiven,acceptedTerms,selfRegistered,providerRegistered,referralDate,lastImport';
            }

            getPatientsPage(appContext, opts).then((result) => {
                return resolve({ data: result.patients, dataLength: result.totalPatients });
            }).catch((err) => {
                console.log("loadContent error:", err);
                reject(err);
            });
        })
    }

    onViewPatient(patientId) {
        const { appContext } = this.props;
        let dialog = <Drawer variant="persistent" anchor="right" open={true}>
            <PatientView
                appContext={appContext}
                patientId={patientId}
                onClose={this.onCloseDialog.bind(this)}
            />
        </Drawer>;
        this.setState({ dialog: dialog });
    }

    onCloseDialog() {
        this.setState({ dialog: null });
        if (this._table) {
            this._table.loadData();
            this._table.setState({ selected: [] });        // unselect the question
        }
    }

    patientSelected(s, t) {
        if (s.length > 0) {
            const patient = t.state.data[s[0]];
            this.onViewPatient(patient.patientId);
        }
        else {
            // nothing selected..
            this.setState({ dialog: null });
        }
    }

    createPatient() {
        const { appContext } = this.props;
        let dialog = <Drawer variant="persistent" anchor="right" open={true}>
            <PatientView
                appContext={appContext}
                onClose={this.onCloseDialog.bind(this)}
            />
        </Drawer>;
        this.setState({ dialog });
    }

    onImportPatients() {
        let dialog = <ImportPatientsDialog appContext={this.props.appContext} onClose={this.onCloseDialog.bind(this)} />;
        this.setState({ dialog });
    }

    onRunDetect() {
        this.setState({
            dialog: <DetectAllDialog appContext={this.props.appContext} onClose={() => {
                this.setState({ dialog: null })
            }} />
        });
    }

    downloadCSV() {
        let { planId, orgId } = this.state;
        this.setState({
            dialog: <ExportPatients appContext={this.props.appContext} planId={planId} orgId={orgId} onClose={() => {
                this.setState({ dialog: null })
            }} />
        });
    }

    importPatientsEMR() {
        const { importOptions: {
            importConditions,
            importMedications,
            importProcedures,
            importDocuments,
            importReports,
            importEncounters,
            fullImport,
            sinceDate,
            startDate,
            endDate
        } } = this.state;
        
        let types = [];
        if ( importConditions ) types.push('Condition');
        if ( importMedications ) types.push('MedicationStatement');
        if ( importProcedures ) types.push('Procedure');
        if ( importDocuments ) types.push('DocumentReference');
        if ( importReports ) types.push('DiagnosticReport');
        if ( importEncounters ) types.push('Encounter');
        types = types.join(',');
        
        this.setState({ progress: <CircularProgress size={20} />, error: null, dialog: null });
        importPatientsEMR(this.props.appContext, { fullImport, types, sinceDate, startDate, endDate, async: true } ).then(() => {
            this.setState({ progress: null });
        }).catch((err) => {
            this.setState({ progress: null, error: getErrorMessage(err) });
        })
    }

    onImportPatientsEMR() {
        const { importOptions } = this.state;
        const { 
            importConditions,
            importMedications,
            importProcedures,
            importDocuments,
            importReports,
            importEncounters,
            fullImport,
            sinceDate,
            startDate,
            endDate
        } = importOptions;

        const handleImportOption = (property, exclude) => (e,v) => {
            const { importOptions } = this.state;
            importOptions[property] = typeof importOptions[property] === 'boolean' ? v : (v ? Moment().format(DATETIME_FORMAT) : undefined);
            if ( Array.isArray(exclude) && exclude.length > 0 ) {
                for(let i=0;i<exclude.length;++i) {
                    const isBoolean = typeof importOptions[exclude[i]] === 'boolean';
                    importOptions[exclude[i]] = isBoolean ? false : undefined;
                }
            }
            this.setState({ importOptions }, this.onImportPatientsEMR.bind(this) )
        };
        const isError = Moment(startDate) > Moment(endDate);
        this.setState({
            dialog: <Dialog open={true}>
                <DialogTitle>EMR IMPORT</DialogTitle>
                <DialogContent>
                    Please confirm you would like to import all EMR data for all patients?<br />
                    <FormControlLabel label='Full Import' control={<Checkbox checked={fullImport} onChange={handleImportOption('fullImport', ['sinceDate', 'startDate', 'endDate'])} />} /><br />
                    <FormControlLabel label='Since Date' control={<Checkbox checked={sinceDate !== undefined} onChange={handleImportOption('sinceDate', ['fullImport', 'startDate', 'endDate'])} />} /><br />
                    {sinceDate && <div><TextField type='datetime-local' value={Moment(sinceDate).format(DATETIME_FORMAT)} onChange={(e) => {
                        if (Moment(e.target.value).isValid()) {
                            importOptions.sinceDate = Moment(e.target.value).format(DATETIME_FORMAT);
                            this.setState({ importOptions }, this.onImportPatientsEMR.bind(this));
                        }
                    }} /></div>}
                    <FormControlLabel label='Start Date' control={<Checkbox checked={startDate !== undefined} onChange={handleImportOption('startDate', ['fullImport', 'sinceDate'])} />} /><br />
                    {startDate && <div><TextField type='datetime-local' value={Moment(startDate).format(DATETIME_FORMAT)} onChange={(e) => {
                        if (Moment(e.target.value).isValid()) {
                            importOptions.startDate = Moment(e.target.value).format(DATETIME_FORMAT);
                            this.setState({ importOptions }, this.onImportPatientsEMR.bind(this));
                        }
                    }} error={isError} helperText={isError ? 'Start date must be before end date.' : ''} /></div>}
                    <FormControlLabel label='End Date' control={<Checkbox checked={endDate !== undefined} onChange={handleImportOption('endDate', ['fullImport', 'sinceDate'])} />} /><br />
                    {endDate && <div><TextField type='datetime-local' value={Moment(endDate).format(DATETIME_FORMAT)} onChange={(e) => {
                        if (Moment(e.target.value).isValid()) {
                            importOptions.endDate = Moment(e.target.value).format(DATETIME_FORMAT);
                            this.setState({ importOptions }, this.onImportPatientsEMR.bind(this));
                        }
                    }} error={isError} helperText={isError ? 'End date must be after start date.' : ''} /></div>}
                    <h4>Import Types</h4>
                    <div style={{marginLeft: 50}}>
                        <FormControlLabel label='Conditions' control={<Checkbox checked={importConditions} onChange={handleImportOption('importConditions')} />} /><br />
                        <FormControlLabel label='Medications' control={<Checkbox checked={importMedications} onChange={handleImportOption('importMedications')} />} /><br />
                        <FormControlLabel label='Procedures' control={<Checkbox checked={importProcedures} onChange={handleImportOption('importProcedures')} />} /><br />
                        <FormControlLabel label='Documents' control={<Checkbox checked={importDocuments} onChange={handleImportOption('importDocuments')} />} /><br />
                        <FormControlLabel label='Diagnostic Reports' control={<Checkbox checked={importReports} onChange={handleImportOption('importReports')} />} /><br />
                        <FormControlLabel label='Encounter' control={<Checkbox checked={importEncounters} onChange={handleImportOption('importEncounters')} />} /><br />
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => this.setState({ dialog: null })}>Cancel</Button>
                    <Button disabled={isError} onClick={this.importPatientsEMR.bind(this)}>Import EMR Data</Button>
                </DialogActions>
            </Dialog>
        });
    }


    render() {
        const { search, dialog, planId, orgId, trialId, showExported, showImported, showInactive, showDates, error, progress } = this.state;

        function isNull(v) {
            return v === '' || v === null || v === undefined || v === 'null';
        }

        const columnData = [
            {
                id: 'patientIds', numeric: false, label: 'Patient IDs', formatValue: (v) => {
                    if (Array.isArray(v)) {
                        let patientIds = [];
                        for (let i = 0; i < v.length; ++i) {
                            const patientId = v[i];
                            if (patientId.systemName.startsWith('org:')) {
                                patientIds.push(patientId.systemId);
                            }
                        }
                        patientIds = patientIds.join(',');
                        if (patientIds.length > 16) {
                            patientIds = patientIds.substring(0, 16) + "...";
                        }
                        return patientIds;
                    } else {
                        return '';
                    }
                }
            },
            { id: 'emrId', label: 'EMR', formatValue: (v) => (isNull(v) ? "No" : "Yes") },
            { id: 'firstName', label: 'First Name' },
            { id: 'lastName', label: 'Last Name' },
            {
                id: 'emails', numeric: false, disabledPadding: false, width: "100", label: 'Emails', formatValue: (v) => {
                    let result = "";
                    for (let i = 0; i < v.length; ++i) {
                        if (v[i].email) {
                            if (result) result += ",";
                            result += v[i].email;
                        }
                    }
                    return result;
                }
            },
            {
                id: 'phoneNumbers', label: 'Phone Numbers', formatValue: (v) => {
                    let result = "";
                    for (let i = 0; i < v.length; ++i) {
                        if (v[i].number) {
                            if (result) result += ",";
                            result += v[i].number;
                        }
                    }
                    return result;
                }
            },
        ];

        if (showDates) {
            columnData.push(
                { id: 'patientCreated', label: 'Patient Created', editType: "dateLabel" },
                { id: 'acceptedTerms', label: 'Accepted Terms', editType: "dateLabel" },
                { id: 'selfRegistered', label: 'Self Registered', editType: "dateLabel" },
                { id: 'providerRegistered', label: 'Provider Registered', editType: "dateLabel" },
                { id: 'referralDate', label: 'Referral Date', editType: "dateLabel" },
                { id: 'lastImport', label: 'Last Import', editType: "dateLabel" }
            );
        }

        return <table style={{ width: '100%' }}><tbody>
            <tr>
                <td>
                    <div style={{ display: 'flex', flexDirection: 'row' }}>
                        <TextField id="searchPatients" style={{ margin: 5, width: 500 }} label='Search' value={search}
                            onChange={(e) => { this.setState({ search: e.target.value }) }}
                            onKeyDown={(e) => { if (e.keyCode === 13) this._table.loadData(); }}
                        />
                        <FormControlLabel control={<Checkbox checked={showInactive}
                            onChange={(e, v) => {
                                this.setState({ showInactive: v }, () => this._table.loadData());
                            }}
                        />} label="Show Inactive" />
                        <FormControlLabel control={<Checkbox checked={showExported}
                            onChange={(e, v) => {
                                this.setState({ showExported: v }, () => this._table.loadData());
                            }}
                        />} label="Show Exported" />
                        <FormControlLabel control={<Checkbox checked={showImported}
                            onChange={(e, v) => {
                                this.setState({ showImported: v }, () => this._table.loadData());
                            }}
                        />} label="Show Imported" />
                        <FormControlLabel control={<Checkbox checked={showDates}
                            onChange={(e, v) => {
                                this.setState({ showDates: v }, () => this._table.loadData());
                            }}
                        />} label="Show Dates" />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'row' }}>
                        <SelectPlan id="selectPlan" label='Select Plan' style={{ margin: 5, width: 300 }} enableAll={true} appContext={this.props.appContext} planId={planId} onChange={(plan) => {
                            this.setState({ planId: plan ? plan.planId : '' }, () => { this._table.loadData() });
                        }} />
                        <SelectOrg id="selectOrg" style={{ width: 300, margin: 5 }} enableAll={true} appContext={this.props.appContext} orgId={orgId} onChange={(orgId) => {
                            this.setState({ orgId }, () => { this._table.loadData() });
                        }} />
                        <SelectTrial id="selectTrial" label='Select Trial' style={{ width: 300, margin: 5 }} appContext={this.props.appContext} trialId={trialId} onChange={(trialId) => {
                            this.setState({ trialId }, () => { this._table.loadData() });
                        }} />
                    </div>
                </td>
                <td align='right'>
                    <span style={{color: 'red'}}>{error}</span>
                    {progress}
                    <Tooltip title='Import EMR for all patients'>
                        <IconButton onClick={this.onImportPatientsEMR.bind(this)}><ImportEMRIcon /></IconButton>
                    </Tooltip>
                    <Tooltip title='Run detect on all patients'>
                        <IconButton onClick={this.onRunDetect.bind(this)}><DetectIcon /></IconButton>
                    </Tooltip>
                </td>
            </tr>
            <tr><td colSpan={2}>
                <EnhancedTable id={`patientData`} disableMultiSelect={true}
                    onActions={(table, numSelected, actions) => {
                        actions.push(<Tooltip key='import' title='Import Scrubbed Patients'>
                            <IconButton onClick={this.onImportPatients.bind(this)}>
                                <ImportIcon />
                            </IconButton>
                        </Tooltip>);
                        actions.push(<Tooltip title='Download Patients CSV'>
                            <IconButton onClick={() => this.downloadCSV()}><DownloadIcon /></IconButton>
                        </Tooltip>)
                    }}
                    onTable={table => this._table = table}
                    onAdd={() => this.createPatient()}
                    onSelected={(s, t) => this.patientSelected(s, t)}
                    orderBy='lastName'
                    rowsPerPage={25}
                    columnData={columnData}
                    data={this.loadContent.bind(this)}
                    title='Patients' />
                {dialog}
            </td></tr>
        </tbody></table>;
    }
}

export default PatientsView;
